import styled from 'styled-components';
import { COLORS } from '../../../../constants/radar';
import { getCompletionRateColor } from '../../../../constants/assignmentCompletion';

export { HeaderStats, HeaderStat, FiltersBar } from '../styles';

// Individual | FT Project | Representative | Expected | Open | Completed | %
//
// No Missing column here: for ONE person it is just
// Expected − (Completed + Open), and the expanded assignment list
// says the same thing more directly — nothing listed IS the missing work.
// It stays on the BU row and in this page's header, where it aggregates into
// a real signal about setup gaps across the unit.
const GRID_COLUMNS = '1.8fr 1.2fr 1.2fr 0.8fr 1fr 0.9fr 0.9fr';

export const GridHeader = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 20px;
  align-items: center;
  padding: 8px 16px 8px 44px; /* 44px leaves room for the expand chevron */
  border-bottom: 2px solid #e2e8f0;

  span {
    font-size: 11px;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    white-space: nowrap;

    &.num {
      text-align: right;
    }

    &.help {
      cursor: help;
      text-decoration: underline dotted #CBD5E1;
      text-underline-offset: 3px;
    }
  }
`;

export const IndividualRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 20px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f8fafc;
  }

  /* Skipped rows are present for context, not for action: same columns, no
     numbers, and no hover/expand affordance. */
  cursor: ${({ $skipped }) => ($skipped ? 'default' : 'pointer')};

  .person {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;

    .chevron {
      color: #94A3B8;
      font-size: 11px;
      width: 12px;
      flex-shrink: 0;
    }

    .identity {
      min-width: 0;
    }

    .name-row {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .name {
      font-size: 14px;
      font-weight: 600;
      color: ${COLORS.PRIMARY};
      cursor: pointer;

      &:hover {
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }

    .skip-tag {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: #64748B;
      background: #F1F5F9;
      border: 1px solid #E2E8F0;
      border-radius: 10px;
      padding: 1px 8px;
      white-space: nowrap;
      cursor: help;

      /* ADR-108: an excluded engagement model is a decision, not a lifecycle
         fact — a warmer tint tells the two reasons apart at a glance */
      &.excluded {
        color: #9A3412;
        background: #FFF7ED;
        border-color: #FED7AA;
        text-transform: none;
        letter-spacing: 0;
        font-size: 11px;
      }
    }

    .detail {
      font-size: 12px;
      color: #64748B;
      margin-top: 1px;
    }
  }

  /* The FT allocation that attributes this person to this BU, and the
     Representative — both plain text, both ellipsised in narrow viewports. */
  .project,
  .manager {
    font-size: 13px;
    color: #475569;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .project.none {
    color: #94A3B8;
  }

  .num {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    text-align: right;

    &.muted {
      color: #94A3B8;
      font-weight: 400;
    }

    /* ADR-096: this person's Expected came from their POSITION's override,
       not the cluster's general rule. Quiet by design — it explains
       a number, it is not a status. */
    .ruleTag {
      display: block;
      font-size: 9px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      color: #94A3B8;
      line-height: 1.2;
      margin-top: 1px;
    }

    &.rate {
      font-weight: 700;
      color: ${({ $rate }) => getCompletionRateColor($rate)};
    }

    /* Work beyond the expectation of its type — real, but crediting nothing */
    .surplus {
      font-size: 11px;
      font-weight: 600;
      color: #94A3B8;
      margin-left: 4px;
    }
  }
`;

// The expanded assignment list for one person
export const AssignmentList = styled.div`
  padding: 6px 16px 12px 44px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;

  .empty {
    font-size: 13px;
    color: #94A3B8;
    font-style: italic;
    padding: 6px 0;
  }
`;

export const AssignmentLine = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.2fr;
  gap: 16px;
  align-items: center;
  padding: 6px 0;

  & + & {
    border-top: 1px dashed #e2e8f0;
  }

  .source {
    font-size: 13px;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    /* Applied only when the reader may actually open the target — the class
       is conditional on read:FeedbackSession, so a name nobody can follow
       never gets a link's affordance (ADR-094 amendment). Styled like the BU
       name on the report row, so a link looks like a link throughout. */
    &.link {
      color: ${COLORS.PRIMARY};
      font-weight: 500;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }
  }

  /* Colours come from constants/reviewTypes via inline props — the system's
     Sequential (blue) / Parallel (green) palette, same as session setup. */
  .type {
    font-size: 11px;
    font-weight: 600;
    border-radius: 10px;
    padding: 2px 8px;
    justify-self: start;

    &.none {
      background: transparent;
      border: none;
      color: #94A3B8;
      font-weight: 400;
      padding-left: 0;
    }
  }

  /* Colours come from the shared assignment-status vocabulary
     (constants/assignmentStatuses) via inline props — one status palette
     across the app, not a second one invented here. */
  .status {
    font-size: 12px;
    font-weight: 600;
    justify-self: start;
    border-radius: 10px;
    padding: 2px 10px;
  }
`;
